import { useTranslation } from 'react-i18next';
import styles from './ChartTypePreview.module.css';
import { getVizTypeConfig } from '../../../constants/vizTypeConfig';

interface ChartTypePreviewProps {
  chartType: string;
}

interface PreviewProps {
  ct: string;
  clr: string;
  bg: string;
}

const MetricPreview = ({ clr }: { clr: string }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.previewMetric}>
      <div className={styles.previewMetricValue} style={{ color: clr }}>2,505</div>
      <div className={styles.previewMetricLabel}>{t('LAYOUT.PREVIEW_SAMPLE_METRIC')}</div>
    </div>
  );
};

const PieDonutPreview = ({ ct, clr, bg }: PreviewProps) => {
  const strokeWidth = ct === 'donut' ? 18 : 40;
  return (
    <div className={styles.previewCenter}>
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r="40" fill="none" stroke={clr} strokeWidth={strokeWidth} strokeDasharray="100 151" strokeDashoffset="25" />
        <circle cx="55" cy="55" r="40" fill="none" stroke={bg} strokeWidth={strokeWidth} strokeDasharray="60 151" strokeDashoffset="-75" />
        <circle cx="55" cy="55" r="40" fill="none" stroke={clr} strokeWidth={strokeWidth} strokeDasharray="40 151" strokeDashoffset="-135" opacity="0.6" />
      </svg>
    </div>
  );
};

const LINE_POINTS_X = [10, 40, 70, 100, 130, 160];
const LINE_POINTS_Y = [65, 40, 50, 20, 35, 15];

const LinePreview = ({ ct, clr }: PreviewProps) => (
  <div className={styles.previewCenter}>
    <svg width="180" height="80" viewBox="0 0 180 80">
      {ct === 'area' && <polygon points="10,65 40,40 70,50 100,20 130,35 160,15 170,65" fill={clr} opacity="0.15" />}
      <polyline points="10,65 40,40 70,50 100,20 130,35 160,15" fill="none" stroke={clr} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {LINE_POINTS_X.map((x, i) => <circle key={x} cx={x} cy={LINE_POINTS_Y[i]} r="3" fill={clr} />)}
    </svg>
  </div>
);

const HBAR_WIDTHS = [80, 60, 90, 45, 70];

const HorizontalBarPreview = ({ clr }: { clr: string }) => (
  <div className={styles.previewHBar}>
    {HBAR_WIDTHS.map((w, i) => (
      <div key={w} className={styles.previewHBarRow}>
        <div className={styles.previewHBarLabel}>Label {i + 1}</div>
        <div className={styles.previewHBarTrack}>
          <div className={styles.previewHBarFill} style={{ width: `${w}%`, background: clr, opacity: 0.6 + i * 0.08 }} />
        </div>
      </div>
    ))}
  </div>
);

const TABLE_PREVIEW_CELLS = [
  { id: 'name', value: 'Name', isHeader: true },
  { id: 'district', value: 'District', isHeader: true },
  { id: 'count', value: 'Count', isHeader: true },
  { id: 'patna', value: 'Patna', isHeader: false },
  { id: 'patna-urban', value: 'Urban', isHeader: false },
  { id: 'patna-count', value: 223, isHeader: false },
  { id: 'gaya', value: 'Gaya', isHeader: false },
  { id: 'gaya-rural', value: 'Rural', isHeader: false },
  { id: 'gaya-count', value: 167, isHeader: false },
  { id: 'saran', value: 'Saran', isHeader: false },
  { id: 'saran-urban', value: 'Urban', isHeader: false },
  { id: 'saran-count', value: 145, isHeader: false },
];

const getTablePreviewCellBackground = (isHeader: boolean, isEvenIndex: boolean, clr: string): string => {
  if (isHeader) return clr;
  return isEvenIndex ? 'var(--bg-surface-secondary)' : 'var(--bg-surface)';
};

const TablePreview = ({ clr }: { clr: string }) => (
  <div className={styles.previewTable}>
    <div className={styles.previewTableGrid}>
      {TABLE_PREVIEW_CELLS.map(({ id, value, isHeader }, i) => (
        <div
          key={id}
          className={styles.previewTableCell}
          style={{
            background: getTablePreviewCellBackground(isHeader, i % 2 === 0, clr),
            color: isHeader ? 'var(--text-on-primary)' : 'var(--text-primary)',
            fontWeight: isHeader ? 600 : 400,
          }}
        >
          {value}
        </div>
      ))}
    </div>
  </div>
);

const SCATTER_POINTS: Array<[number, number, number]> = [
  [30, 60, 6], [60, 30, 9], [90, 55, 5], [120, 20, 8], [45, 70, 4], [100, 40, 7],
];

const ScatterPreview = ({ ct, clr }: PreviewProps) => (
  <div className={styles.previewCenter}>
    <svg width="160" height="90" viewBox="0 0 160 90">
      <line x1="10" y1="80" x2="150" y2="80" stroke="var(--border-subtle)" strokeWidth="1" />
      <line x1="10" y1="10" x2="10" y2="80" stroke="var(--border-subtle)" strokeWidth="1" />
      {SCATTER_POINTS.map(([x, y, r], i) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={ct === 'bubble' ? r + 2 : r} fill={clr} opacity={0.4 + i * 0.08} />
      ))}
    </svg>
  </div>
);

const BAR_HEIGHTS = [60, 40, 80, 55, 70, 45, 90];

const BarPreview = ({ clr }: { clr: string }) => (
  <div className={styles.previewBar}>
    {BAR_HEIGHTS.map((h, i) => (
      <div key={h} className={styles.previewBarCol} style={{ height: `${h}%`, background: clr, opacity: 0.5 + i * 0.07 }} />
    ))}
  </div>
);

const HISTOGRAM_HEIGHTS = [25, 45, 70, 95, 80, 50, 30, 15];

const HistogramPreview = ({ clr }: { clr: string }) => (
  <div className={styles.previewHistogram}>
    {HISTOGRAM_HEIGHTS.map((h) => (
      <div key={h} className={styles.previewHistogramCol} style={{ height: `${h}%`, background: clr, opacity: 0.85 }} />
    ))}
  </div>
);

const MULTIBAR_GROUPS = [[55, 75], [40, 60], [70, 50], [50, 85]];

const MultibarPreview = ({ clr }: { clr: string }) => (
  <div className={styles.previewMultibar}>
    {MULTIBAR_GROUPS.map((group) => (
      <div key={group.join('-')} className={styles.previewMultibarGroup}>
        {group.map((h, j) => (
          <div key={h} className={styles.previewMultibarCol} style={{ height: `${h}%`, background: clr, opacity: j === 0 ? 0.9 : 0.45 }} />
        ))}
      </div>
    ))}
  </div>
);

const STACKEDBAR_GROUPS = [
  { id: 'g1', segments: [{ id: 'g1-0', height: 35 }, { id: 'g1-1', height: 25 }, { id: 'g1-2', height: 20 }] },
  { id: 'g2', segments: [{ id: 'g2-0', height: 20 }, { id: 'g2-1', height: 40 }, { id: 'g2-2', height: 15 }] },
  { id: 'g3', segments: [{ id: 'g3-0', height: 30 }, { id: 'g3-1', height: 20 }, { id: 'g3-2', height: 35 }] },
  { id: 'g4', segments: [{ id: 'g4-0', height: 25 }, { id: 'g4-1', height: 30 }, { id: 'g4-2', height: 25 }] },
];

const StackedBarPreview = ({ clr }: { clr: string }) => (
  <div className={styles.previewStackedBar}>
    {STACKEDBAR_GROUPS.map(({ id, segments }) => (
      <div key={id} className={styles.previewStackedBarCol}>
        {segments.map(({ id: segmentId, height }, j) => (
          <div key={segmentId} style={{ height: `${height}%`, background: clr, opacity: 0.4 + j * 0.3 }} />
        ))}
      </div>
    ))}
  </div>
);

const HEATMAP_OPACITIES = [0.9, 0.3, 0.6, 0.15, 0.8, 0.4, 0.7, 0.9, 0.1, 0.5, 0.3, 0.6, 0.85, 0.2, 0.4, 0.7, 0.9, 0.5, 0.25, 0.6];
const HEATMAP_CELLS = HEATMAP_OPACITIES.map((opacity, i) => ({ id: `heatmap-${i}`, opacity }));

const HeatmapPreview = ({ clr }: { clr: string }) => (
  <div className={styles.previewHeatmap}>
    {HEATMAP_CELLS.map(({ id, opacity }) => (
      <div key={id} className={styles.previewHeatmapCell} style={{ background: clr, opacity }} />
    ))}
  </div>
);

const FUNNEL_WIDTHS = [140, 108, 78, 50];
const FUNNEL_PCTS = ['100%', '68%', '42%', '21%'];

const FunnelPreview = ({ ct, clr }: PreviewProps) => (
  <div className={styles.previewCenter}>
    <svg width="160" height="120" viewBox="0 0 160 120">
      {FUNNEL_WIDTHS.map((w, i) => {
        const nextW = FUNNEL_WIDTHS[i + 1] ?? w * 0.6;
        const y = i * 27;
        const x1 = (160 - w) / 2;
        const x2 = (160 - nextW) / 2;
        return (
          <g key={w}>
            <polygon points={`${x1},${y} ${x1 + w},${y} ${x2 + nextW},${y + 24} ${x2},${y + 24}`} fill={clr} opacity={0.9 - i * 0.15} />
            {ct === 'conversionfunnel' && (
              <text x={80} y={y + 16} textAnchor="middle" fontSize="11" fill="var(--text-on-primary)">{FUNNEL_PCTS[i]}</text>
            )}
          </g>
        );
      })}
    </svg>
  </div>
);

const STAGE_LABELS = ['Stage 1', 'Stage 2', 'Stage 3', 'Stage 4'];

const StageFunnelPreview = ({ clr }: { clr: string }) => (
  <div className={styles.previewStages}>
    {STAGE_LABELS.map((s, i) => (
      <div key={s} className={styles.previewStageChevron} style={{ background: clr, opacity: 0.9 - i * 0.15 }}>{s}</div>
    ))}
  </div>
);

const MAP_POINTS: Array<[number, number]> = [[40, 35], [95, 50], [135, 30], [60, 78], [150, 82], [100, 92]];

const MapPreview = ({ clr, bg }: { clr: string; bg: string }) => (
  <div className={styles.previewCenter}>
    <svg width="180" height="110" viewBox="0 0 180 110">
      <rect x="4" y="4" width="172" height="102" rx="10" fill={bg} />
      {MAP_POINTS.map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <circle cx={x} cy={y} r="9" fill={clr} opacity="0.25" />
          <circle cx={x} cy={y} r="5" fill={clr} opacity="0.9" />
        </g>
      ))}
    </svg>
  </div>
);

const DefaultPreview = ({ icon, label, clr }: { icon: string; label: string; clr: string }) => {
  const { t } = useTranslation();
  return (
    <div className={styles.previewIcon}>
      <span className="material-icons" style={{ fontSize: 48, color: clr }}>{icon}</span>
      <span className={styles.previewIconLabel}>{t('CHART_PREVIEW.NOT_AVAILABLE', { label })}</span>
    </div>
  );
};

const PREVIEW_RENDERERS: Record<string, (props: PreviewProps) => JSX.Element> = {
  metric: ({ clr }) => <MetricPreview clr={clr} />,
  pie: (props) => <PieDonutPreview {...props} />,
  donut: (props) => <PieDonutPreview {...props} />,
  line: (props) => <LinePreview {...props} />,
  area: (props) => <LinePreview {...props} />,
  multiline: (props) => <LinePreview {...props} />,
  horizontalbar: ({ clr }) => <HorizontalBarPreview clr={clr} />,
  table: ({ clr }) => <TablePreview clr={clr} />,
  tablebar: ({ clr }) => <TablePreview clr={clr} />,
  scatter: (props) => <ScatterPreview {...props} />,
  bubble: (props) => <ScatterPreview {...props} />,
  bar: ({ clr }) => <BarPreview clr={clr} />,
  histogram: ({ clr }) => <HistogramPreview clr={clr} />,
  multibar: ({ clr }) => <MultibarPreview clr={clr} />,
  stackedbar: ({ clr }) => <StackedBarPreview clr={clr} />,
  heatmap: ({ clr }) => <HeatmapPreview clr={clr} />,
  funnel: (props) => <FunnelPreview {...props} />,
  conversionfunnel: (props) => <FunnelPreview {...props} />,
  stagefunnel: ({ clr }) => <StageFunnelPreview clr={clr} />,
  map: ({ clr, bg }) => <MapPreview clr={clr} bg={bg} />,
};

/**
 * Decorative sample-data preview for a chart type — used wherever a user is
 * picking/reviewing a chart type (visualization Info tab, dashboard Layout
 * "Add Visualization" modal) so every entry point shows the same reference
 * design instead of each screen inventing its own mock.
 */
export const ChartTypePreview = ({ chartType }: ChartTypePreviewProps) => {
  const ct = (chartType || '').toLowerCase();
  const cfg = getVizTypeConfig(chartType);
  const clr = cfg.color || '#5F5E5A';
  const bg = cfg.bg || '#F1EFE8';

  const renderPreview = PREVIEW_RENDERERS[ct];
  if (renderPreview) return renderPreview({ ct, clr, bg });

  return <DefaultPreview icon={cfg.icon} label={cfg.label} clr={clr} />;
};
